import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BUSINESS_PROFILE, SCORED_SECTIONS, ALL_QUESTIONS, computeScore, questionScore } from "@/lib/trcaSchema";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, ShieldCheck, ClipboardList, Sparkles } from "lucide-react";

const STORAGE_KEY = "twin.trca.draft.v1";

const EMPTY = {
  contact: { name: "", company_name: "", email: "", phone: "" },
  bp: { business_type: [], industries: [], industries_other: "", annual_turnover: "", tender_volume: "", win_rate: "" },
  answers: Object.fromEntries(ALL_QUESTIONS.map((q) => [q.key, []])),
};

// Step 0 = intro, 1 = contact, 2 = business profile, 3-8 = sections, 9 = review/submit
const TOTAL_STEPS = 8; // visible step indicator: contact (1) + bp (2) + 6 scored sections (3-8)

export default function Trca() {
  const nav = useNavigate();
  const { user, booting } = useAuth();
  const [step, setStep] = useState(0); // 0 = intro
  const [state, setState] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [gateChecked, setGateChecked] = useState(false);

  // Eligibility gate: must be authenticated AND have a paid credit (trca_credits > 0).
  useEffect(() => {
    if (booting) return;
    if (!user) {
      nav("/login?next=/trca", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/trca/me/status");
        if (!data.can_take) {
          nav("/trca-qualifier", { replace: true });
          return;
        }
      } catch {}
      setGateChecked(true);
    })();
  }, [user, booting, nav]);

  // Load draft on mount + pre-fill contact details from user account
  useEffect(() => {
    if (!gateChecked) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        setState({ ...EMPTY, ...saved });
        return;
      }
    } catch {}
    if (user) {
      setState((s) => ({
        ...s,
        contact: { ...s.contact, name: user.name || "", email: user.email || "" },
      }));
    }
  }, [gateChecked, user]);

  // Persist draft
  useEffect(() => {
    if (step > 0) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
    }
  }, [state, step]);

  const liveScore = useMemo(() => computeScore(state.answers).total, [state.answers]);

  const goNext = () => {
    const errs = validateStep(step, state);
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const goBack = () => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    setSubmitErr("");
    setSubmitting(true);
    try {
      const payload = {
        name: state.contact.name,
        company_name: state.contact.company_name,
        email: state.contact.email,
        phone: state.contact.phone,
        business_profile: state.bp,
        answers: state.answers,
      };
      const { data } = await api.post("/trca/submit", payload);
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.setItem("twin.trca.lastResult", JSON.stringify(data));
      nav(`/trca/results/${data.id}`);
    } catch (ex) {
      const detail = ex.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "payment_required") {
        nav("/trca-qualifier", { replace: true });
        return;
      }
      setSubmitErr(formatApiErrorDetail(detail) || ex.message);
      setSubmitting(false);
    }
  };

  if (booting || !gateChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xs uppercase tracking-wider text-ink-muted">Loading…</div>
      </div>
    );
  }

  if (step === 0) return <Intro onStart={() => setStep(1)} hasDraft={!!localStorage.getItem(STORAGE_KEY)} liveScore={liveScore} onReset={() => { setState(EMPTY); localStorage.removeItem(STORAGE_KEY); }} />;

  return (
    <div className="min-h-screen bg-background">
      <TopBar step={step} total={TOTAL_STEPS} liveScore={liveScore} />

      <div className="max-w-3xl mx-auto px-6 py-10 pb-32 animate-fade-up" data-testid="trca-form">
        {step === 1 && <ContactStep state={state} setState={setState} errors={errors} />}
        {step === 2 && <BusinessProfileStep state={state} setState={setState} errors={errors} />}
        {step >= 3 && step <= 8 && (
          <SectionStep section={SCORED_SECTIONS[step - 3]} state={state} setState={setState} stepLabel={`Section ${step} of ${TOTAL_STEPS}`} />
        )}

        {submitErr && (
          <div className="mt-6 text-sm text-warn bg-warn-soft border border-warn/30 px-4 py-3 rounded-sm flex items-start gap-2" data-testid="submit-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {submitErr}
          </div>
        )}
      </div>

      {/* Sticky nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-ink-line z-40">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button
            onClick={goBack}
            disabled={step === 1}
            data-testid="trca-back"
            className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted font-num">
            Step {step} / {TOTAL_STEPS} · {liveScore}/100
          </div>
          {step < TOTAL_STEPS ? (
            <button onClick={goNext} data-testid="trca-next" className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="trca-submit" className="px-5 py-2 text-sm bg-warn hover:bg-warn/90 disabled:opacity-60 text-white font-medium rounded-sm flex items-center gap-1.5 transition-colors">
              <CheckCircle2 className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit assessment"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function validateStep(step, state) {
  const errs = {};
  if (step === 1) {
    if (!state.contact.name.trim()) errs.name = "Name is required";
    if (!state.contact.company_name.trim()) errs.company_name = "Company name is required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(state.contact.email)) errs.email = "Valid email required";
    if (!/^[\d+\-\s]{4,}$/.test(state.contact.phone.trim())) errs.phone = "Valid phone required (digits, +, -, spaces)";
  }
  if (step === 2) {
    if (!state.bp.win_rate) errs.win_rate = "Please select your current win rate";
  }
  return errs;
}

function TopBar({ step, total, liveScore }) {
  const pct = Math.round((step / total) * 100);
  return (
    <div className="sticky top-0 z-30 bg-white border-b border-ink-line">
      <div className="max-w-3xl mx-auto px-6 py-3 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="trca-home-link">
          <div className="w-7 h-7 bg-brand text-white grid place-items-center font-display font-bold text-sm rounded-sm">T</div>
          <div className="font-display font-bold text-sm tracking-tight">TRCA</div>
        </Link>
        <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
          <div className="h-full bg-brand transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[11px] uppercase tracking-wider text-ink-muted font-num whitespace-nowrap">
          {step}/{total}
        </div>
      </div>
    </div>
  );
}

function Intro({ onStart, hasDraft, liveScore, onReset }) {
  return (
    <div className="min-h-screen bg-background" data-testid="trca-intro">
      <div className="border-b border-ink-line bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-brand grid place-items-center text-white font-display font-bold tracking-tight rounded-sm">T</div>
            <div className="leading-none">
              <div className="font-display font-bold text-[15px] tracking-tight">TWIN</div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-ink-muted mt-1">Tender Intelligence</div>
            </div>
          </Link>
          <Link to="/" className="text-[13px] text-ink-muted hover:text-ink">← Back to site</Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 chip border-brand/30 bg-brand-soft text-brand mb-6">
            <Sparkles className="w-3 h-3" /> <span>Paid assessment · ~7 minutes</span>
          </div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95]">
            Tender Readiness <br /><span className="text-brand">Capability Assessment.</span>
          </h1>
          <p className="text-ink-muted text-base lg:text-lg mt-6 leading-relaxed max-w-xl">
            Score your firm against 20 capability questions across 6 disciplines. Get a readiness band, identify gaps,
            and receive a tailored consulting recommendation from our team within 2 business days.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
            {[
              { icon: ClipboardList, n: "20", l: "Capability questions" },
              { icon: ShieldCheck, n: "6", l: "Scored sections" },
              { icon: CheckCircle2, n: "/100", l: "Readiness score" },
            ].map((x) => (
              <div key={x.l} className="border border-ink-line p-4 rounded-sm bg-white">
                <x.icon className="w-4 h-4 text-brand mb-2" />
                <div className="font-num text-2xl font-bold">{x.n}</div>
                <div className="text-[11px] uppercase tracking-wider text-ink-muted mt-1">{x.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-3 flex-wrap">
            <button onClick={onStart} data-testid="trca-start-btn" className="bg-warn hover:bg-warn/90 text-white px-6 py-3 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors">
              {hasDraft ? "Resume assessment" : "Start assessment"} <ArrowRight className="w-4 h-4" />
            </button>
            {hasDraft && (
              <button onClick={onReset} data-testid="trca-reset-btn" className="px-5 py-3 text-sm border border-ink-line rounded-sm hover:bg-secondary transition-colors">
                Start fresh
              </button>
            )}
            {hasDraft && (
              <div className="text-xs text-ink-muted">Draft progress saved · current score {liveScore}/100</div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 card-flat p-6">
          <div className="stat-label">What you'll get</div>
          <ul className="mt-4 space-y-3 text-sm">
            {[
              "A 0-100 readiness score",
              "Section-by-section breakdown across 6 disciplines",
              "Your readiness band: Emerging, Developing, Established or Advanced",
              "A copy of your full submission",
              "A call-back from our team within 2 business days",
            ].map((x) => (
              <li key={x} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-good mt-0.5 shrink-0" /> <span>{x}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 pt-6 border-t border-ink-line text-[11px] uppercase tracking-wider text-ink-muted">
            Your answers are confidential. We use them only to advise your team.
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldShell({ label, error, required, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="stat-label block mb-1.5">
        {label} {required && <span className="text-warn">*</span>}
      </label>
      {children}
      {error && <div className="text-xs text-warn mt-1.5 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {error}</div>}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand";

function ContactStep({ state, setState, errors }) {
  const set = (k) => (e) => setState((s) => ({ ...s, contact: { ...s.contact, [k]: e.target.value } }));
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Page 1 of 8</div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-1">Contact details</h1>
        <p className="text-ink-muted text-sm mt-2">Tell us who's filling this out. We'll only use this to share your score.</p>
      </div>

      <div className="card-flat p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FieldShell label="Full name" required error={errors.name} testid="trca-field-name">
          <input value={state.contact.name} onChange={set("name")} className={inputCls} data-testid="trca-name-input" />
        </FieldShell>
        <FieldShell label="Company name" required error={errors.company_name} testid="trca-field-company">
          <input value={state.contact.company_name} onChange={set("company_name")} className={inputCls} data-testid="trca-company-input" />
        </FieldShell>
        <FieldShell label="Work email" required error={errors.email} testid="trca-field-email">
          <input type="email" value={state.contact.email} onChange={set("email")} className={inputCls} data-testid="trca-email-input" />
        </FieldShell>
        <FieldShell label="Phone number" required error={errors.phone} testid="trca-field-phone">
          <input value={state.contact.phone} onChange={set("phone")} placeholder="+91 98xxxxxxxx" className={inputCls} data-testid="trca-phone-input" />
        </FieldShell>
      </div>
    </div>
  );
}

function BusinessProfileStep({ state, setState, errors }) {
  const toggleMulti = (k, opt) => setState((s) => {
    const cur = new Set(s.bp[k] || []);
    if (cur.has(opt)) cur.delete(opt); else cur.add(opt);
    return { ...s, bp: { ...s.bp, [k]: [...cur] } };
  });
  const setSingle = (k, opt) => setState((s) => ({ ...s, bp: { ...s.bp, [k]: opt } }));
  const setOther = (val) => setState((s) => ({ ...s, bp: { ...s.bp, industries_other: val } }));

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Page 2 of 8</div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mt-1">Business profile</h1>
        <p className="text-ink-muted text-sm mt-2">Context for our team. Not part of your score.</p>
      </div>

      <div className="card-flat p-6 space-y-7">
        {Object.entries(BUSINESS_PROFILE).map(([k, meta]) => (
          <FieldShell key={k} label={meta.label} required={meta.required} error={errors[k]} testid={`trca-bp-${k}`}>
            {meta.type === "multi" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {meta.options.map((opt) => (
                  <CheckboxItem key={opt} label={opt} checked={(state.bp[k] || []).includes(opt)} onChange={() => toggleMulti(k, opt)} testid={`trca-bp-${k}-${slug(opt)}`} />
                ))}
              </div>
            )}
            {meta.type === "multi_with_other" && (
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {meta.options.map((opt) => (
                    <CheckboxItem key={opt} label={opt} checked={(state.bp[k] || []).includes(opt)} onChange={() => toggleMulti(k, opt)} testid={`trca-bp-${k}-${slug(opt)}`} />
                  ))}
                </div>
                <input
                  placeholder="Other (free text)"
                  value={state.bp.industries_other || ""}
                  onChange={(e) => setOther(e.target.value)}
                  className={inputCls + " mt-2"}
                  data-testid="trca-bp-industries-other"
                />
              </div>
            )}
            {meta.type === "single" && (
              <div className="grid grid-cols-1 gap-2">
                {meta.options.map((opt) => (
                  <RadioItem key={opt} name={k} label={opt} checked={state.bp[k] === opt} onChange={() => setSingle(k, opt)} testid={`trca-bp-${k}-${slug(opt)}`} />
                ))}
              </div>
            )}
          </FieldShell>
        ))}
      </div>
    </div>
  );
}

function SectionStep({ section, state, setState, stepLabel }) {
  const toggle = (qk, opt) => setState((s) => {
    const cur = new Set(s.answers[qk] || []);
    if (cur.has(opt)) cur.delete(opt); else cur.add(opt);
    return { ...s, answers: { ...s.answers, [qk]: [...cur] } };
  });
  const sectionScore = Math.round(section.questions.reduce((acc, q) => acc + questionScore(q, state.answers[q.key]), 0) * 100) / 100;

  return (
    <div className="space-y-6" data-testid={`trca-section-${section.key}`}>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">{stepLabel}</div>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-1">
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">{section.title}</h1>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted font-num">
            Section score: <span className="text-brand font-bold">{sectionScore}</span> / {section.max}
          </div>
        </div>
        <p className="text-ink-muted text-sm mt-2">Select all that apply. Each option you check adds to your score.</p>
      </div>

      <div className="space-y-4">
        {section.questions.map((q, i) => (
          <div key={q.key} className="card-flat p-6" data-testid={`trca-question-${q.key}`}>
            <div className="flex items-start justify-between gap-4 mb-1">
              <div className="flex items-baseline gap-2">
                <div className="font-num text-[11px] text-ink-muted uppercase tracking-wider">{q.key.toUpperCase()}</div>
                <div className="font-display font-bold text-base tracking-tight">{q.label}</div>
              </div>
              <div className="chip border-brand/30 bg-brand-soft text-brand font-num">
                {state.answers[q.key]?.length || 0} / {q.options.length}
              </div>
            </div>
            <p className="text-sm text-ink-muted mb-4">{q.prompt}</p>
            <div className="space-y-2">
              {q.options.map((opt) => (
                <CheckboxItem key={opt} label={opt} checked={(state.answers[q.key] || []).includes(opt)} onChange={() => toggle(q.key, opt)} testid={`trca-${q.key}-${slug(opt)}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CheckboxItem({ label, checked, onChange, testid }) {
  return (
    <label className={`flex items-start gap-3 px-3 py-2.5 border rounded-sm cursor-pointer transition-colors ${checked ? "bg-brand-soft border-brand/40" : "border-ink-line hover:bg-secondary"}`} data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={onChange} className="mt-0.5 accent-brand w-4 h-4 shrink-0" />
      <span className="text-sm leading-snug">{label}</span>
    </label>
  );
}

function RadioItem({ name, label, checked, onChange, testid }) {
  return (
    <label className={`flex items-start gap-3 px-3 py-2.5 border rounded-sm cursor-pointer transition-colors ${checked ? "bg-brand-soft border-brand/40" : "border-ink-line hover:bg-secondary"}`} data-testid={testid}>
      <input type="radio" name={name} checked={checked} onChange={onChange} className="mt-0.5 accent-brand w-4 h-4 shrink-0" />
      <span className="text-sm leading-snug">{label}</span>
    </label>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}
